import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { useDeleteDocument } from "@/features/documents/hooks/use-documents";
import type { Document } from "@/features/documents/types";
import { useTranslation } from "@/features/utils/i18n";
import { TrashIcon } from "@phosphor-icons/react";
import { useMemo } from "react";

export const useDocumentsBulkActions = (): BulkAction<Document>[] => {
  const { t } = useTranslation("documents");
  const isOwner = useHasRole("OWNER");
  const deleteDocument = useDeleteDocument();

  return useMemo(() => {
    if (!isOwner) return [];

    return [
      {
        key: "delete",
        label: t("actions.deleteSelected"),
        icon: <TrashIcon className="h-4 w-4" />,
        variant: "destructive",
        confirm: (rows) =>
          t("messages.bulkDeleteConfirm", { count: rows.length }),
        onAction: async (rows) => {
          await runBulk(
            rows,
            (document) => deleteDocument.mutateAsync(document.id),
            {
              success: (count) => t("messages.bulkDeleteSuccess", { count }),
              error: (count) => t("messages.bulkDeleteError", { count }),
            },
          );
        },
      },
    ];
  }, [isOwner, t, deleteDocument]);
};
